import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import wretch from "wretch";
import moment from "moment";
import { NotificationsContext } from "../../__state/Notifications";
import { ACCT_PLAN_LEVELS } from "../../utils/constants";

import "./HeaderDefault.scss";

import LogoWhite from "../../__assets/img/qlogo-white.svg";
import Clock from "react-live-clock";
import HelpIcon from "@mui/icons-material/Help";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

const HeaderDefault = ({ setIsLoggingOut }) => {
  const navigate = useNavigate();
  
  // Store user data without causing re-renders
  const userDataRef = useRef(JSON.parse(sessionStorage.getItem("selfData")));
  const { username, firstName, planLevel, token } = userDataRef.current;

  const [showOptions, setShowOptions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { notifications, setNotifications } = useContext(NotificationsContext);
  
  const intervalRef = useRef(null); //  Keep track of active interval

  /** 
   * Fetch notifications - this function is stable due to useCallback
   */
  const getUserNotifications = useCallback(async () => {
    if (!token || !username) return; //  Ensure required values exist

    try {
      const loadedNotifications = await wretch(
        `${process.env.REACT_APP_UTIL_API_URL}/user/notifications/${username}`
      )
        .auth(`Bearer ${token}`)
        .get()
        .json();

      loadedNotifications.sort((a, b) => moment(b.timestamp) - moment(a.timestamp));
      setNotifications([...loadedNotifications]);
    } catch (error) {
      console.log(error);
      if (error.text && JSON.parse(error.text).type === "token") {
        alert("Your session has expired. Please log in again.");
        return;
      }
      console.error("Can't get notifications.");
    }
  }, [setNotifications, token, username]); //  Only `setNotifications` is a dependency

  /**
   * Set up periodic fetching ONCE on mount
   */
  useEffect(() => {
    getUserNotifications(); //  Initial fetch on mount

    if (intervalRef.current) {
      clearInterval(intervalRef.current); //  Ensure no duplicate intervals
    }

    intervalRef.current = setInterval(() => {
      getUserNotifications();
    }, 1000 * 60 * 5); //  Every 5 minutes

    return () => clearInterval(intervalRef.current); //  Cleanup on unmount
  }, []); //  Empty dependency array = only runs once

  // Toggle settings dropdown
  const toggleSettings = () => {
    setShowOptions((prev) => !prev);
  };

  // Logout function
  const logout = () => {
    if (setIsLoggingOut) {
      setIsLoggingOut(true);
    }

    sessionStorage.removeItem("selfData");
    sessionStorage.removeItem("cachedDevices");
    navigate("/"); // Redirect to login page
  };

  return (
    <div className="header-default-component">
      <div className="header-left">
        <img alt="IntelliSense Systems" src={LogoWhite} />
      </div>
      <div className="header-right">
        <div className="time-notify" data-plan-basic={planLevel === ACCT_PLAN_LEVELS.BASIC}>
          <div className="time-wrapper">
            <Clock format={"ddd, DD MMM YYYY HH:mm:ss"} timezone={"UTC"} ticking={true} />
            <span>&nbsp;UTC</span>
          </div>
          {planLevel !== ACCT_PLAN_LEVELS.BASIC && (
            <div className="notifications">
              <div className="click-wrapper" role="button" onClick={() => setShowNotifications(true)}>
                <NotificationsIcon />
              </div>
              {notifications.length > 0 && (
                <div className="notifications-count">
                  {notifications.length > 99 ? "99+" : notifications.length}
                </div>
              )}
            </div>
          )}
          {showNotifications && (
            <ClickAwayListener onClickAway={() => setShowNotifications(false)}>
              <Paper className="notify-popup">
                <div className="list-wrapper">
                  <List dense={true}>
                    {notifications.slice(0, 9).map(({ imei, rule, actual, timestamp, threshold, title, unit }, i) => {
                      let statement = "";
                      const unitText = unit === "null" ? "" : ` ${unit}`;
                      const secondary = moment(timestamp).format("YYYY-MM-DD HH:mm:ss");

                      if (title.toLowerCase() === "tamper") {
                        statement = `${imei} was tampered`;
                      } else {
                        statement = `${imei} was ${rule} ${threshold}${unitText} for ${title.toLowerCase()}, reaching ${actual.replace(
                          /SM/gi,
                          ""
                        )}${unitText}.`;
                      }

                      return (
                        <div key={i}>
                          <ListItem>
                            <ListItemText primary={statement} secondary={secondary} />
                          </ListItem>
                          {i < 8 && <Divider />}
                        </div>
                      );
                    })}
                  </List>
                  <div className="manage-all">
                    <Link to="/account/notifications">Manage notifications</Link>
                  </div>
                </div>
              </Paper>
            </ClickAwayListener>
          )}
        </div>
        <div className="options" onClick={toggleSettings}>
          <span>{firstName || "User"}</span>
          <div className="header-icon-bg">
            <PersonOutlineIcon />
          </div>
          <span className="header-arrow">
            {showOptions ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </span>
        </div>
      </div>
      <div className={`options-container ${showOptions ? "show" : ""}`}>
        <Link to="/account">
          <div role="button" className="user-option my-account">
            <span>My Account</span>
            <div className="header-icon-bg">
              <SettingsIcon />
            </div>
          </div>
        </Link>
        <Link to="/help">
          <div role="button" className="user-option help">
            <span>Help</span>
            <div className="help-icon">
              <HelpIcon />
            </div>
          </div>
        </Link>
        <div role="button" className="user-option" onClick={logout}>
          <span>Sign Out</span>
          <div className="header-icon-bg">
            <ExitToAppIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderDefault;
